/**
 * The client half of shell sync (ALU-168).
 *
 * The shell (classic DashboardShell or EncuentroFrame) and the `data-ui` on
 * `<html>` come from layouts, which a client navigation doesn't re-render.
 * When the global switch flips (or rolls back) while a tab or the native app
 * is open, the next navigation would put the other interface's page inside
 * the old shell, unstyled, until a reload.
 *
 * proxy.ts stamps UI_VERSION_HEADER on every router request (proxy-hint.ts).
 * This module wraps `fetch` (Next's router and server actions call the global
 * at call time) and compares the header with the shell's version:
 *
 * - navigation or refresh: a full load of the target URL instead of the soft
 *   navigation. The router's promise is held, so the old page stays on screen
 *   until the browser leaves it; nothing of the other interface is painted.
 * - server action: the action already ran; a full reload of the current page.
 * - prefetch: nothing loads yet. The mismatch is remembered and the next
 *   navigation (onNavigationStart, wired to onRouterTransitionStart) becomes
 *   a full load, so a prefetched loading state never paints either.
 *
 * Loop guard: one full load per target version per SHELL_RELOAD_COOLDOWN_MS
 * per tab (sessionStorage). A reload is only ever triggered by a navigation
 * or an action, never on page load, so it cannot loop by itself; the guard
 * also stops a stale hint (the 60 s render cache after a manual SQL change)
 * from turning every click into a full load. A load whose shell matches the
 * mark clears it, so a later rollback reloads again.
 *
 * The shell is the root layout's stamp (SHELL_VERSION_ATTRIBUTE), never the
 * live `data-ui`, which a page may set itself (ALU-304).
 *
 * Works the same in the Capacitor WebView (same bundle, same router).
 * WebDeploySync also checks the header on its resume probe.
 */
import {
  safeGetSessionItem,
  safeRemoveSessionItem,
  safeSetSessionItem,
} from '@/lib/utils/safe-storage'
import { ENCUENTRO_SCOPE_ATTRIBUTE, ENCUENTRO_SCOPE_VALUE } from '@/lib/design/encuentro-scope'
import { parseUiVersion, type UiVersion } from './resolve'

export const UI_VERSION_HEADER = 'x-alumnia-ui-version'
export const SHELL_RELOAD_KEY = 'alumnia:ui-shell-reload'
export const SHELL_RELOAD_COOLDOWN_MS = 60_000
/** How long a router promise is held after asking for a full load, in case the browser stays (a beforeunload prompt the user cancels). */
export const SHELL_RELOAD_HOLD_MS = 4_000

/** Next's cache-busting param on router requests (NEXT_RSC_UNION_QUERY). */
const RSC_MARKER_PARAM = '_rsc'

export type RouterRequestKind = 'navigation' | 'prefetch' | 'action'
export type ShellReloadTrigger = RouterRequestKind | 'resume'

/** Which kind of Next router request these headers belong to, if any. */
export function routerRequestKind(headers: Headers): RouterRequestKind | null {
  if (headers.has('next-action')) return 'action'
  if (headers.has('next-router-prefetch') || headers.has('next-router-segment-prefetch')) {
    return 'prefetch'
  }
  if (headers.has('rsc')) return 'navigation'
  return null
}

/**
 * Stamped on `<html>` by the root layout with the version it rendered, and
 * written by nothing else. `data-ui` is not enough: a page that follows the
 * public site instead of the switch (`/start`, `/start/confirm`, the
 * marketing pages) puts the Encuentro scope on `<html>` itself after
 * hydration (HtmlScope), in classic too. Read as the shell, that made every
 * server action on those pages look like a flip, and the reload it caused
 * landed on a document whose `data-ui` was classic again until HtmlScope ran,
 * which cleared the loop guard's mark: an endless reload (ALU-304).
 */
export const SHELL_VERSION_ATTRIBUTE = 'data-ui-shell'

/**
 * The version the current document's shell was rendered with: the root
 * layout's stamp, or `data-ui` for a document without one (a tab from an
 * older build, the global error page).
 */
export function shellVersionOf(doc: Pick<Document, 'documentElement'>): UiVersion {
  const stamped = parseUiVersion(doc.documentElement.getAttribute(SHELL_VERSION_ATTRIBUTE))
  if (stamped) return stamped
  return doc.documentElement.getAttribute(ENCUENTRO_SCOPE_ATTRIBUTE) === ENCUENTRO_SCOPE_VALUE
    ? 'encuentro'
    : 'classic'
}

/** The page URL behind a router request URL (drops Next's `_rsc` marker). */
export function pageUrlOf(requestUrl: string, base: string): string {
  const url = new URL(requestUrl, base)
  url.searchParams.delete(RSC_MARKER_PARAM)
  return url.toString()
}

type ReloadMark = { to: UiVersion; at: number }

export function parseReloadMark(raw: string | null): ReloadMark | null {
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) return null
    const { to, at } = value as { to?: unknown; at?: unknown }
    const version = parseUiVersion(to)
    return version && typeof at === 'number' ? { to: version, at } : null
  } catch {
    return null
  }
}

/** The loop guard: no second full load toward the same version within the cooldown. */
export function mayReloadToward(mark: ReloadMark | null, to: UiVersion, now: number): boolean {
  return !mark || mark.to !== to || now - mark.at >= SHELL_RELOAD_COOLDOWN_MS
}

type ShellWindow = Pick<Window, 'location' | 'document' | 'setTimeout'> & {
  fetch: typeof fetch
}

export type ShellReloadEvent = {
  trigger: ShellReloadTrigger
  from: UiVersion
  to: UiVersion
}

type Options = {
  onReload?: (event: ShellReloadEvent) => void
  now?: () => number
}

/**
 * Full load of `target` when `serverVersion` differs from this document's
 * shell and the guard allows it. Returns whether a load was started.
 */
export function reloadIfShellStale(
  win: Pick<ShellWindow, 'location' | 'document'>,
  target: string,
  serverVersion: UiVersion | null,
  trigger: ShellReloadTrigger,
  { onReload, now = Date.now }: Options = {}
): boolean {
  if (!serverVersion) return false
  const shell = shellVersionOf(win.document)
  if (serverVersion === shell) return false
  const at = now()
  if (!mayReloadToward(parseReloadMark(safeGetSessionItem(SHELL_RELOAD_KEY)), serverVersion, at)) {
    return false
  }
  safeSetSessionItem(SHELL_RELOAD_KEY, JSON.stringify({ to: serverVersion, at }))
  try {
    onReload?.({ trigger, from: shell, to: serverVersion })
  } catch {
    // Telemetry never blocks the reload.
  }
  if (target === win.location.href) win.location.reload()
  else win.location.assign(target)
  return true
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  if (init?.headers) return new Headers(init.headers)
  if (typeof Request !== 'undefined' && input instanceof Request) return input.headers
  return new Headers()
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

/**
 * Wraps `win.fetch` once. Returns the hook for Next's onRouterTransitionStart.
 */
export function installUiShellSync(win: ShellWindow, options: Options = {}) {
  // A load whose shell is the version we reloaded toward worked: clear the
  // mark so a later flip (or rollback) can reload again.
  const mark = parseReloadMark(safeGetSessionItem(SHELL_RELOAD_KEY))
  if (mark && mark.to === shellVersionOf(win.document)) safeRemoveSessionItem(SHELL_RELOAD_KEY)

  let prefetchedVersion: UiVersion | null = null
  const originalFetch = win.fetch

  const hold = (response: Response) =>
    new Promise<Response>((resolve) => {
      win.setTimeout(() => resolve(response), SHELL_RELOAD_HOLD_MS)
    })

  win.fetch = async function shellSyncFetch(input: RequestInfo | URL, init?: RequestInit) {
    const response = await originalFetch.call(win, input, init)
    let kind: RouterRequestKind | null = null
    try {
      kind = routerRequestKind(requestHeaders(input, init))
      if (!kind) return response
      const serverVersion = parseUiVersion(response.headers.get(UI_VERSION_HEADER))
      if (!serverVersion) return response
      if (kind === 'prefetch') {
        prefetchedVersion = serverVersion === shellVersionOf(win.document) ? null : serverVersion
        return response
      }
      const target =
        kind === 'action'
          ? win.location.href
          : pageUrlOf(response.url || requestUrl(input), win.location.href)
      if (reloadIfShellStale(win, target, serverVersion, kind, options)) return hold(response)
    } catch {
      // Never break a request over the check.
    }
    return response
  } as typeof fetch

  return {
    /** Next's onRouterTransitionStart: a prefetch already saw the other version. */
    onNavigationStart(url: string) {
      if (!prefetchedVersion) return
      const version = prefetchedVersion
      prefetchedVersion = null
      try {
        reloadIfShellStale(
          win,
          new URL(url, win.location.href).toString(),
          version,
          'prefetch',
          options
        )
      } catch {
        // Never break a navigation over the check.
      }
    },
  }
}
